import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useChatWidget } from '../../contexts/ChatWidgetContext';
import HelpRecommendationService from '../../services/helpRecommendationService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const HelpSupportPage = () => {
  const { userProfile } = useAuth();
  const { openChat } = useChatWidget();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [error, setError] = useState(null);

  // Help categories
  const helpCategories = [
    { id: 'all', label: 'All Topics', icon: 'Grid' },
    { id: 'getting-started', label: 'Getting Started', icon: 'Play' },
    { id: 'profile', label: 'Profile & Settings', icon: 'User' },
    { id: 'jobs', label: 'Jobs & Applications', icon: 'Briefcase' },
    { id: 'messaging', label: 'Communication', icon: 'MessageSquare' },
    { id: 'payment', label: 'Payment & Billing', icon: 'CreditCard' },
    { id: 'technical', label: 'Technical Issues', icon: 'Settings' },
    { id: 'policies', label: 'Policies & Guidelines', icon: 'Shield' }
  ];

  // Get user role for filtering
  const userRole = userProfile?.role || 'va';

  // Handle contact support
  const handleContactSupport = () => {
    openChat();
  };

  // Get urgency color
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Load all published articles from database
  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await HelpRecommendationService.getPersonalizedSuggestions(userProfile);
      if (result.success && result.data.length > 0) {
        setArticles(result.data);
      } else {
        setError('No articles available');
        setArticles([]);
      }
    } catch (err) {
      console.error('Error loading articles:', err);
      setError('Failed to load articles');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  // Filter articles based on search and category
  const filterArticles = useCallback(() => {
    let filtered = articles;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.description.toLowerCase().includes(query) ||
        (article.tags && article.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    setFilteredArticles(filtered);
  }, [articles, selectedCategory, searchQuery]);

  // Load articles on mount
  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  // Filter articles when search or category changes
  useEffect(() => {
    filterArticles();
  }, [filterArticles]);

  // Handle article click
  const handleArticleClick = (article) => {
    // Navigate to article detail page using slug
    if (article.slug) {
      navigate(`/help-support/${article.slug}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-16 pb-20 md:pb-8">
          <div className="content-container">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg border border-border hover:border-primary hover:bg-primary-50 transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Help & Support</h1>
                <p className="text-text-secondary mt-1">
                  Get help with common questions and issues
                </p>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search help topics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {helpCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-4">
              <Button
                variant="outline"
                iconName="MessageCircle"
                onClick={handleContactSupport}
              >
                Contact Support
              </Button>
              <Button
                variant="outline"
                iconName="Book"
                onClick={() => alert('FAQ section would be implemented here')}
              >
                View FAQ
              </Button>
              <Button
                variant="outline"
                iconName="Video"
                onClick={() => alert('Video tutorials would be implemented here')}
              >
                Video Tutorials
              </Button>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {helpCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-border'
                  }`}
                >
                  <Icon name={category.icon} size={16} />
                  <span>{category.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Suggestions Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredArticles.map(article => (
              <div
                key={article.id}
                className="bg-white rounded-lg border border-border p-6 hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => handleArticleClick(article)}
              >
                {/* Thumbnail */}
                {article.thumbnail_url && (
                  <div className="mb-4 -mx-6 -mt-6">
                    <img
                      src={article.thumbnail_url}
                      alt={article.title}
                      className="w-full h-40 object-cover rounded-t-lg"
                    />
                  </div>
                )}

                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                      <Icon name={article.icon || 'HelpCircle'} size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors truncate">
                        {article.title}
                      </h3>
                      {article.estimatedTime && (
                        <span className="text-sm text-text-muted">{article.estimatedTime}</span>
                      )}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium border whitespace-nowrap ml-2 ${getUrgencyColor(article.urgency)}`}>
                    {article.urgency}
                  </div>
                </div>

                {/* Description */}
                <p className="text-text-secondary text-sm mb-4 line-clamp-2">
                  {article.description}
                </p>

                {/* Stats */}
                <div className="flex items-center space-x-4 text-xs text-text-muted mb-4 pb-4 border-b border-gray-100">
                  {article.view_count !== undefined && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Eye" size={14} />
                      <span>{article.view_count} views</span>
                    </div>
                  )}
                  {article.helpful_count !== undefined && (
                    <div className="flex items-center space-x-1">
                      <Icon name="ThumbsUp" size={14} />
                      <span>{article.helpful_count} helpful</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {article.tags.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Action */}
                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm text-text-muted">Click to read</span>
                  <Icon name="ArrowRight" size={16} className="text-primary group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>

          {/* No Results */}
          {!loading && filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                No help topics found
              </h3>
              <p className="text-text-secondary mb-4">
                Try adjusting your search or browse different categories
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
              >
                Clear filters
              </Button>
            </div>
          )}

          {/* Support Contact Section */}
          <div className="mt-12 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-8 text-center">
            <Icon name="HeadphonesIcon" size={48} className="mx-auto text-primary mb-4" />
            <h3 className="text-xl font-semibold text-text-primary mb-2">
              Still need help?
            </h3>
            <p className="text-text-secondary mb-6">
              Our support team is here to help you with any questions or issues
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="primary"
                iconName="MessageCircle"
                onClick={handleContactSupport}
              >
                Start Live Chat
              </Button>
              <Button
                variant="outline"
                iconName="Mail"
                onClick={() => window.location.href = 'mailto:support@vasprouts.com'}
              >
                Email Support
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpSupportPage;
